import { GLOSSARY } from "./glossary";

// The Product Feeds function id; each feed is a saved task of this function.
export const PRODUCT_FEEDS_FUNCTION_ID = "product_feeds";

// Default copy for the Product Feeds pages. Contentful can override any of it
// through PRODUCT_FEEDS_CONTENT.
export const DEFAULT_CONTENT = {
    title: "Product Feeds",
    description: "Build an Adobe Target Recommendations product feed from your product APIs and push it to Adobe Target.",
    create_feed: "Create feed",
    back_to_saved_tasks: "Back to saved tasks",
    save_draft: "Save draft",
    discard_draft: "Discard draft",
    preview: "Preview",
    publish: "Publish changes",
    run_now: "Run now",
    unsaved_changes: "You have unsaved changes.",
    unpublished_changes: "Draft has unpublished changes",
};

export const TABS = [
    { id: "sources", label: "Sources", tip: GLOSSARY.tabs.sources },
    { id: "fields", label: "Fields", tip: GLOSSARY.tabs.fields },
    { id: "columns", label: "CSV columns", tip: GLOSSARY.tabs.columns },
    { id: "preview", label: "Preview", tip: GLOSSARY.tabs.preview },
    { id: "schedule", label: "Schedule & delivery", tip: GLOSSARY.tabs.schedule },
    { id: "runs", label: "Runs", tip: GLOSSARY.tabs.runs },
];

export const SOURCE_STEPS = [
    { id: "request", label: "1. Request", tip: GLOSSARY.sourceSteps.request },
    { id: "structure", label: "2. Structure", tip: GLOSSARY.sourceSteps.structure },
    { id: "mapping", label: "3. Map fields", tip: GLOSSARY.sourceSteps.mapping },
    { id: "role", label: "4. Role & failure", tip: GLOSSARY.sourceSteps.role },
];

// How often pages refresh while a build or push is in progress.
export const POLL_MS = 5000;

// What a feed is doing now (feed.activity.stage from the API).
export const ACTIVITY_LABELS = {
    starting: "Starting build",
    building: "Building",
    pushing: "Pushing to Target",
};

export const OUTCOME_STYLES = {
    published: "bg-green-100 text-green-800",
    held: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-100 text-red-700",
    running: "bg-blue-100 text-blue-700",
    pushed: "bg-green-100 text-green-800",
    partial: "bg-yellow-100 text-yellow-800",
};

export const DELIVERY_LABELS = {
    api: "Adobe Target API (Stride pushes products)",
    url: "Feed URL (Target pulls the CSV)",
};

export const ZERO_INVENTORY_LABELS = {
    send: "Send as 0",
    delete: "Delete from Target",
    skip: "Don't send",
};

export const RELATION_LABELS = {
    contained: "Contained (variants are inside each product)",
    key: "Key (separate lists linked by an id)",
    group: "Group (flat rows grouped into products)",
    self: "Self (each product is its own variant)",
    zip: "Zip (parallel arrays on the product)",
    cartesian: "Cartesian (every combination of option lists)",
};

export const INTERVAL_LABELS = {
    off: "Manual only",
    "12h": "Every 12 hours",
    "24h": "Every day",
    "7d": "Every week",
    "14d": "Every two weeks",
};

export const ROLE_LABELS = {
    key: "Key",
    name: "Name",
    price: "Price",
    availability: "Availability",
    image: "Image",
    url: "URL",
    category: "Category",
    brand: "Brand",
    default_variant_ref: "Default variant ref",
    default_variant_match: "Default variant match",
};

export const COLUMN_KIND_LABELS = {
    product: "Product field",
    aggregate: "Aggregate of variants",
    list: "List (one value per variant)",
    indexed: "Numbered columns (name1…nameN)",
    variants_json: "Variants JSON",
    representative: "Field from one variant",
    template: "Template",
    const: "Fixed value",
    fallback: "First non-empty of…",
};

// Which variant a "Field from one variant" column reads.
export const COLUMN_VARIANT_LABELS = {
    representative: "Representative variant",
    price: "Price variant (same as entity.value)",
};

export const COLUMN_VARIANT_HELP = {
    representative: "Picked by the representative rules on the Fields tab",
    price: "The variant entity.value comes from: the cheapest variant under the feed's price rule",
};

// entity.value is locked to one of these rules (settings.price_rule).
export const PRICE_RULE_LABELS = {
    lowest_available: "Lowest available price",
    lowest_any: "Lowest price of any variant",
};

export const PRICE_RULE_HELP = {
    lowest_available: "entity.value is the lowest price among available variants, or the lowest price of any variant when none is available.",
    lowest_any: "entity.value is the lowest price of any variant, available or not.",
};

// "What is this column for?" shortcuts in the add-column dialog.
export const COLUMN_PURPOSES = [
    { id: "criteria", label: "Filter in criteria or inclusion rules", column: { kind: "aggregate", op: "distinct" } },
    { id: "velocity", label: "Velocity design (one value per variant)", column: { kind: "list" } },
    { id: "slots", label: "Velocity design (numbered slots)", column: { kind: "indexed", count: 3 } },
    { id: "javascript", label: "JavaScript design (variants JSON)", column: { kind: "variants_json", fields: [], on_limit: "drop_fields" } },
    { id: "product", label: "A single product value", column: { kind: "product" } },
    { id: "price_variant", label: "A value from the variant that sets entity.value", column: { kind: "representative", variant: "price" } },
];

export const PAGINATION_FIELDS = {
    page: ["param", "start", "size", "size_param", "total_pages_path", "items_path", "max_pages"],
    offset: ["param", "limit_param", "limit", "start_offset", "total_path", "items_path", "max_pages"],
    cursor: ["param", "cursor_path", "has_more_path", "items_path", "max_pages"],
    next_url: ["next_path", "max_pages"],
    link_header: ["max_pages"],
    none: [],
};

export const PAGINATION_FIELD_HELP = {
    param: "Query parameter name",
    start: "First page number (default 1)",
    size: "Page size (stops when a page is shorter)",
    size_param: "Page size parameter name",
    limit_param: "Limit parameter name",
    limit: "Items per request",
    start_offset: "First offset (default 0)",
    total_pages_path: "Path to the total page count",
    total_path: "Path to the total item count",
    items_path: "Path to the items (defaults to the product path)",
    cursor_path: "Path to the next cursor",
    has_more_path: "Path to the has-more flag",
    next_path: "Path to the next page URL",
    max_pages: "Maximum pages (default 100)",
};

export const newSourceId = (sources = []) => {
    const used = new Set(sources.map((s) => s.id));
    let n = sources.length + 1;
    while (used.has(`src_${n}`)) n++;
    return `src_${n}`;
};

export const createSource = (sources, preset) => ({
    id: newSourceId(sources),
    name: `Source ${sources.length + 1}`,
    role: sources.some((s) => s.role === "primary") ? "enrichment" : "primary",
    request: { method: "GET", url: "", query: [], headers: [], format: "json", pagination: { type: "none" } },
    prepare: [],
    extract: preset?.extract ? JSON.parse(JSON.stringify(preset.extract)) : {
        products: { steps: [{ select: "$.products[*]", as: "product" }] },
        variants: { steps: [{ from: "product", select: "@.variants[*]", as: "variant" }], relation: { type: "contained" } },
    },
    product_map: {},
    variant_map: {},
    failure: {},
});
